import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { DataTable } from '../ui/data-table';
import CreateUserModal from '../ui/CreateUserModal';
import ProfileModal from '../ui/ProfileModal';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { classAPI, courseAPI, classStudentAPI, userAPI, topicAPI, cacheUtils } from '../../services/api';

const ClassDetails = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [course, setCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCourseContentModalOpen, setIsCourseContentModalOpen] = useState(false);
  const [isMarkInstructorModalOpen, setIsMarkInstructorModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [courseTopics, setCourseTopics] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [courseContentLoading, setCourseContentLoading] = useState(false);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [isMarkingInstructor, setIsMarkingInstructor] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);

  useEffect(() => {
    fetchClassDetails();
    fetchStudents();
    fetchAllUsers();
  }, [classId]);

  const fetchClassDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch class details
      const classResponse = await classAPI.getClassById(classId);
      const classInfo = classResponse.data;
      setClassData(classInfo);
      
      // Fetch course details if course_id exists
      if (classInfo.course_id) {
        try {
          const courseResponse = await courseAPI.getAllCourses();
          const courses = courseResponse.data || [];
          const foundCourse = courses.find(c => c.id === classInfo.course_id);
          setCourse(foundCourse);
        } catch (courseError) {
          console.error('Error fetching course details:', courseError);
        }
      }
    } catch (error) {
      console.error('Error fetching class details:', error);
      setError('Failed to load class details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      setStudentsLoading(true);
      const response = await classStudentAPI.getStudentsInClass(classId);
      console.log('Fetched students response:', response);
      console.log('Students data:', response.data);
      console.log('First student object:', response.data?.[0]);
      console.log('Student data structure:', JSON.stringify(response.data?.[0], null, 2));
      setStudents(response.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      console.log('Fetching all users...');
      const response = await userAPI.getAllStudents();
      console.log('Raw API response:', response);
      console.log('Users data:', response.data);
      console.log('Users type:', typeof response.data);
      console.log('Users is array:', Array.isArray(response.data));
      
      const users = response.data || [];
      setAllUsers(users);
      console.log('Set allUsers to:', users);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      // Set empty array on error to prevent undefined issues
      setAllUsers([]);
    }
  };

  const handleEnrollStudent = async (userData) => {
    try {
      setIsEnrolling(true);
      // Find the user by ID from userData
      const userId = userData.id;
      
      const enrollmentData = {
        class_id: parseInt(classId),
        student_id: userId,
      };
      
      await classStudentAPI.enrollStudent(enrollmentData);
      
      // Refresh the students list
      await fetchStudents();
      
      setIsAddUserModalOpen(false);
    } catch (error) {
      console.error('Error enrolling student:', error);
      throw error;
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleViewProfile = async (student) => {
    try {
      setProfileLoading(true);
      setSelectedUser(student);
      setIsProfileModalOpen(true);
      
      // Fetch user profile
      const response = await userAPI.getUserProfile(student.email);
      setSelectedUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setSelectedUserProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleViewCourseContent = async () => {
    if (!classData?.course_id) {
      console.error('No course ID available');
      return;
    }

    try {
      setCourseContentLoading(true);
      setIsCourseContentModalOpen(true);
      
      // Fetch course topics with lessons
      const response = await topicAPI.getTopicsWithLessonsByCourseId(classData.course_id, classData.id);
      console.log('Course topics with lessons:', response.data);
      setCourseTopics(response.data || []);
    } catch (error) {
      console.error('Error fetching course content:', error);
      setCourseTopics([]);
    } finally {
      setCourseContentLoading(false);
    }
  };

  const handleRefreshCourseContent = async () => {
    if (!classData?.course_id) {
      console.error('No course ID available');
      return;
    }

    try {
      setCourseContentLoading(true);
      
      // Fetch course topics with lessons
      const response = await topicAPI.getTopicsWithLessonsByCourseId(classData.course_id, classData.id);
      console.log('Refreshed course topics with lessons:', response.data);
      setCourseTopics(response.data || []);
    } catch (error) {
      console.error('Error refreshing course content:', error);
      setCourseTopics([]);
    } finally {
      setCourseContentLoading(false);
    }
  };

  const handleMarkInstructor = async (topic) => {
    try {
      setSelectedLesson(topic);
      setIsMarkInstructorModalOpen(true);
      setInstructorsLoading(true);
      
      // Fetch instructors list
      const response = await userAPI.getInstructors();
      console.log('Instructors response:', response.data);
      setInstructors(response.data || []);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      setInstructors([]);
    } finally {
      setInstructorsLoading(false);
    }
  };

  const handleConfirmMarkInstructor = async () => {
    if (!selectedLesson || !selectedInstructor || !classData) {
      return;
    }

    try {
      setIsMarkingInstructor(true);
      
      const payload = {
        class_id: parseInt(classData.id),
        topic_id: parseInt(selectedLesson.topic_id),
        instructor_id: parseInt(selectedInstructor.id),
        instructor_name: `${selectedInstructor.first_name} ${selectedInstructor.last_name}`
      };

      
      console.log('Marking instructor with payload:', payload);

      await classAPI.checkInstructorForLesson(payload);
      // Close modal and reset state
      setIsMarkInstructorModalOpen(false);
      setSelectedLesson(null);
      setSelectedInstructor(null);
      setInstructors([]);
      
      // Show success message
      alert('Instructor marked successfully for the lesson!');
      
      // Invalidate cache and refresh course content to show updated instructor information
      if (classData?.course_id) {
        try {
          setCourseContentLoading(true);
          
          // Clear relevant cache entries to ensure fresh data
          cacheUtils.clearTopicCache();
          cacheUtils.clearClassCache();
          
          const response = await topicAPI.getTopicsWithLessonsByCourseId(classData.course_id, classData.id);
          console.log('Refreshed course topics with lessons after cache invalidation:', response.data);
          setCourseTopics(response.data || []);
        } catch (error) {
          console.error('Error refreshing course content:', error);
        } finally {
          setCourseContentLoading(false);
        }
      }
      
    } catch (error) {
      console.error('Error marking instructor:', error);
      alert('Failed to mark instructor. Please try again.');
    } finally {
      setIsMarkingInstructor(false);
    }
  };

  const handleDeleteStudent = async () => {
    try {
      console.log('Removing student from class:', selectedUser);

      const studentId = selectedUser.student_id || selectedUser.student?.id;
      await classStudentAPI.deleteStudentFromClass(classId, studentId);
      
      // Refresh the students list
      await fetchStudents();
      
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error removing student from class:', error);
    }
  };

  const openDeleteModal = (student) => {
    setSelectedUser(student);
    setIsDeleteModalOpen(true);
  };

  const handleGoBack = () => {
    navigate('/classes');
  };

  const studentColumns = [
    {
      accessorKey: 'student_id',
      header: 'Student ID',
      sortable: true,
      cell: (student) => student.student_id || student.student?.id || 'N/A'
    },
    {
      accessorKey: 'first_name',
      header: 'First Name',
      sortable: true,
      cell: (student) => student.first_name || student.student?.first_name || 'N/A'
    },
    {
      accessorKey: 'last_name',
      header: 'Last Name',
      sortable: true,
      cell: (student) => student.last_name || student.student?.last_name || 'N/A'
    },
    {
      accessorKey: 'email',
      header: 'Email',
      sortable: true,
      cell: (student) => student.email || student.student?.email || 'N/A'
    },
    {
      accessorKey: 'role',
      header: 'Role',
      sortable: true,
      cell: (student) => student.role || student.student?.role || 'student'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: (student) => (
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewProfile(student)}
            className="h-8 w-8 p-0"
            title="View Profile"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openDeleteModal(student)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Remove Student"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading class details...</p>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="w-full p-6">
        <div className="text-center py-8">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Class Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested class could not be found.'}</p>
          <Button onClick={handleGoBack} className="bg-blue-600 hover:bg-blue-700">
            Back to Classes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleGoBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to classes"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Class Details</h1>
              <p className="text-lg text-gray-600">View detailed information about this class</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid - Class Info Left, Students Right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column - Class Information Card */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden sticky top-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
              <h2 className="text-2xl font-bold text-white">{classData.title}</h2>
              <p className="text-blue-100">Class ID: {classData.class_id || 'N/A'}</p>
            </div>
            <div className="p-6">
              {/* Basic Information */}
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Basic Information
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Title</label>
                    <p className="text-gray-900">{classData.title || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-900">{classData.description || 'No description available'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Class ID</label>
                    <p className="text-gray-900 font-mono">{classData.class_id || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Class Code</label>
                    <p className="text-gray-900 font-mono">{classData.class_code || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Course Information */}
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Course Information
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Course</label>
                    <p className="text-gray-900">{course ? course.title : 'Unknown Course'}</p>
                  </div>
                  
                  {course && course.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Course Description</label>
                      <p className="text-gray-900">{course.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Course ID</label>
                    <p className="text-gray-900">{classData.course_id || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* Metadata */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Metadata</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Internal ID</label>
                    <p className="text-gray-900 font-mono">{classData.id}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Created At</label>
                    <p className="text-gray-900">
                      {classData.created_at ? new Date(classData.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Updated At</label>
                    <p className="text-gray-900">
                      {classData.updated_at ? new Date(classData.updated_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Right Column - Students Section */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-blue-600 px-6 py-4">
              <div className="mb-3">
                  <h2 className="text-2xl font-bold text-white">Class Students</h2>
                  <p className="text-green-100">Manage students enrolled in this class</p>
              </div>
              <div className="space-y-2">
                {/* First Row - 3 Buttons */}
                <div className="flex space-x-3">
                  <Button
                    onClick={handleViewCourseContent}
                    className="bg-white text-green-600 hover:bg-green-50"
                    disabled={!classData?.course_id}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    See Course Content
                  </Button>
                  <Button
                    onClick={() => {
                      setIsAddUserModalOpen(true);
                      fetchAllUsers(); // Fetch fresh user data when modal opens
                    }}
                    className="bg-white text-green-600 hover:bg-green-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Student
                  </Button>
                  <Button
                    onClick={() => navigate(`/classes/${classId}/attendance`, { state: { classId: classData.id, classTitle: classData.title } })}
                    className="bg-white text-green-600 hover:bg-green-50"
                  >
                    Class Attendance
                  </Button>
                  <Button
                    onClick={() => navigate(`/classes/${classId}/feedback`, { state: { classId: classData.id, classTitle: classData.title } })}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Class Feedback
                  </Button>
                </div>
                {/* Second Row - 3 Buttons */}
                <div className="flex space-x-3">
                  
                  <Button
                    onClick={() => navigate(`/classes/${classId}/shared-files`, { state: { classId: classData.id, classTitle: classData.title } })}
                    className="bg-white text-indigo-600 hover:bg-indigo-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Share Files
                  </Button>
                  <Button
                    onClick={() => navigate(`/classes/${classId}/assignments`, { state: { classId: classData.id, classTitle: classData.title } })}
                    className="bg-white text-orange-600 hover:bg-orange-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Assignments
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <DataTable
                columns={studentColumns}
                data={students}
                searchKey="first_name"
                searchPlaceholder="Search students..."
                loading={studentsLoading}
                emptyMessage="No students enrolled in this class."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      <EnrollStudentModal 
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onEnroll={handleEnrollStudent}
        allUsers={allUsers}
        enrolledStudents={students}
        isEnrolling={isEnrolling}
      />

      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedUser(null);
          setSelectedUserProfile(null);
        }}
        profileData={selectedUserProfile}
        loading={profileLoading}
      />

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteModalOpen} 
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        className="max-w-md"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Remove Student</h2>
        </ModalHeader>
        
        <ModalBody>Course Content
          <p className="text-gray-600">
            Are you sure you want to remove{' '}
            <span className="font-semibold">
              {selectedUser?.first_name} {selectedUser?.last_name}
            </span>{' '}
            from this class? This action cannot be undone.
          </p>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsDeleteModalOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteStudent}
            className="bg-red-600 hover:bg-red-700"
          >
            Remove Student
          </Button>
        </ModalFooter>
      </Modal>

      {/* Course Content Modal */}
      <Modal 
        isOpen={isCourseContentModalOpen} 
        onClose={() => {
          setIsCourseContentModalOpen(false);
          setCourseTopics([]);
        }}
        className="max-w-5xl h-[80vh] flex flex-col"
      >
        <ModalHeader>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Course Content</h2>
              <p className="text-gray-600">
                {course ? `${course.title} - Topics and Lessons` : 'Course Topics and Lessons'}
              </p>
            </div>
            {/* <Button
              onClick={handleRefreshCourseContent}
              disabled={courseContentLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              size="sm"
            >
              {courseContentLoading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Refreshing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </Button> */}
          </div>
        </ModalHeader>
        
        <ModalBody className="flex-1 overflow-y-auto">
          {courseContentLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Loading course content...</p>
            </div>
          ) : courseTopics.length > 0 ? (
            <div className="space-y-6">
              {(() => {
                // Deduplicate topics by topic_id and aggregate instructor names
                const topicMap = new Map();
                courseTopics.forEach(topic => {
                  if (!topicMap.has(topic.topic_id)) {
                    topicMap.set(topic.topic_id, {
                      ...topic,
                      instructors: topic.instructor_name ? [topic.instructor_name] : []
                    });
                  } else {
                    const existing = topicMap.get(topic.topic_id);
                    if (topic.instructor_name && !existing.instructors.includes(topic.instructor_name)) {
                      existing.instructors.push(topic.instructor_name);
                    }
                  }
                });
                const uniqueTopics = Array.from(topicMap.values()).sort((a, b) => a.topic_id - b.topic_id);

                return uniqueTopics.map((topic, topicIndex) => (
                  <div key={topic.topic_id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white">
                          Topic {topicIndex + 1}. {topic.topic_title}
                        </h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Topic ID: {topic.topic_id}</p>
                          {topic.instructors.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {topic.instructors.map((name, idx) => (
                                <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <Button
                            size="sm"
                            className="bg-white text-green-600 hover:bg-green-50"
                            onClick={() => handleMarkInstructor(topic)}
                          >
                            Mark Instructor
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Course Content Found</h3>
              <p>This course doesn't have any topics or lessons yet.</p>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsCourseContentModalOpen(false);
              setCourseTopics([]);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Mark Instructor Modal */}
      <Modal 
        isOpen={isMarkInstructorModalOpen} 
        onClose={() => {
          setIsMarkInstructorModalOpen(false);
          setSelectedLesson(null);
          setSelectedInstructor(null);
          setInstructors([]);
        }}
        className="max-w-2xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Mark Instructor for Topic</h2>
          <p className="text-gray-600">
            {selectedLesson ? `Select an instructor for: ${selectedLesson.topic_title}` : 'Select an instructor for this topic'}
          </p>
        </ModalHeader>
        
        <ModalBody className="space-y-4">
          {selectedLesson && (
            <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="font-medium text-purple-900">{selectedLesson.topic_title}</p>
                  <p className="text-sm text-purple-600">Topic ID: {selectedLesson.topic_id}</p>
                </div>
              </div>
            </div>
          )}

          {instructorsLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              <p className="mt-2 text-gray-600">Loading instructors...</p>
            </div>
          ) : (
            <div className='overflow-hidden'>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Instructor ({instructors.length} available)
              </label>
              
              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-md">
                {instructors.length > 0 ? (
                  <div className="space-y-1 p-2">
                    {instructors.map(instructor => (
                      <div
                        key={instructor.id}
                        className={`p-3 rounded-md cursor-pointer transition-colors ${
                          selectedInstructor?.id === instructor.id
                            ? 'bg-purple-100 border-purple-300 border'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                        onClick={() => setSelectedInstructor(instructor)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {instructor.first_name} {instructor.last_name}
                            </p>
                            <p className="text-sm text-gray-600">{instructor.email}</p>
                            {instructor.department && (
                              <p className="text-sm text-gray-500">Department: {instructor.department}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              Instructor
                            </span>
                            {instructor.specialization && (
                              <p className="text-xs text-gray-500 mt-1">{instructor.specialization}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    <p>No instructors available</p>
                    <p className="text-xs mt-1">Please contact the administrator to add instructors.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedInstructor && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm text-green-700">
                <strong>Selected Instructor:</strong> {selectedInstructor.first_name} {selectedInstructor.last_name}
              </p>
              <p className="text-sm text-green-600">{selectedInstructor.email}</p>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button 
            variant="outline"
            onClick={() => {
              setIsMarkInstructorModalOpen(false);
              setSelectedLesson(null);
              setSelectedInstructor(null);
              setInstructors([]);
            }}
            disabled={isMarkingInstructor}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmMarkInstructor}
            disabled={!selectedInstructor || isMarkingInstructor}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isMarkingInstructor ? 'Marking...' : 'Mark Instructor'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// EnrollStudentModal Component
const EnrollStudentModal = ({ isOpen, onClose, onEnroll, allUsers, enrolledStudents, isEnrolling }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  // Debug logging
  console.log('EnrollStudentModal props:', {
    isOpen,
    allUsersCount: allUsers?.length || 0,
    allUsers: allUsers,
    enrolledStudentsCount: enrolledStudents?.length || 0,
    enrolledStudents: enrolledStudents
  });

  // Filter out users who are already enrolled
  const enrolledStudentIds = enrolledStudents.map(student => student.student_id);
  const availableUsers = allUsers.filter(user => !enrolledStudentIds.includes(user.id));

  console.log('Available users after filtering:', {
    enrolledStudentIds,
    availableUsersCount: availableUsers?.length || 0,
    availableUsers: availableUsers
  });

  // Filter users based on search term
  const filteredUsers = availableUsers.filter(user =>
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.student_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('Filtered users:', {
    searchTerm,
    filteredUsersCount: filteredUsers?.length || 0,
    filteredUsers: filteredUsers
  });

  const handleEnroll = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      await onEnroll(selectedUser);
      setSelectedUser(null);
      setSearchTerm('');
    } catch (err) {
      setError(err.message || 'Failed to enroll student');
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSearchTerm('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-2xl">
      <ModalHeader>
        <h2 className="text-xl font-semibold text-gray-900">Enroll Student</h2>
        <p className="text-gray-600">Select a user to enroll in this class</p>
      </ModalHeader>
      
      <ModalBody className="space-y-4">
        {/* Debug Info */}
        <div className="bg-gray-100 p-3 rounded text-sm">
          <p><strong>Debug Info:</strong></p>
          <p>All Users: {allUsers?.length || 0}</p>
          <p>Enrolled Students: {enrolledStudents?.length || 0}</p>
          <p>Available Users: {availableUsers?.length || 0}</p>
          <p>Filtered Users: {filteredUsers?.length || 0}</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        {/* Search Input */}
        <div>
          <input
            type="text"
            placeholder="Search users by name, email, or student number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* User Selection */}
        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-md">
          {filteredUsers.length > 0 ? (
            <div className="space-y-1 p-2">
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className={`p-3 rounded-md cursor-pointer transition-colors ${
                    selectedUser?.id === user.id
                      ? 'bg-blue-100 border-blue-300 border'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                  onClick={() => setSelectedUser(user)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      {user.student_number && (
                        <p className="text-sm text-gray-500">Student #: {user.student_number}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {user.role || 'user'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              {allUsers?.length === 0 ? (
                <div>
                  <p>No users loaded from the API.</p>
                  <p className="text-xs mt-2">Check console for API errors.</p>
                </div>
              ) : availableUsers?.length === 0 ? (
                <div>
                  <p>All users are already enrolled in this class.</p>
                </div>
              ) : (
                <div>
                  <p>{searchTerm ? 'No users found matching your search.' : 'No users available for enrollment.'}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-700">
              <strong>Selected:</strong> {selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button 
          variant="outline" 
          onClick={handleClose}
          disabled={isEnrolling}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleEnroll}
          disabled={!selectedUser || isEnrolling}
          className="bg-green-600 hover:bg-green-700"
        >
          {isEnrolling ? 'Enrolling...' : 'Enroll Student'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClassDetails;
